import { GameStateEngine } from '@/core/game-state-engine'
import { HudLocator } from '@/core/hud-locator'
import { drawSourceToImageData } from '@/core/image'
import { ReadingValidator } from '@/core/reading-validator'
import { captureIntervalMs } from '@/core/recognition-settings'
import type { CaptureProfile, GameEvent, GameStateSnapshot, RawReading, ValidatedObservation } from '@/domain/types'
import type { GamePreset, HudLocation, PresetRecognizer } from '@/presets/types'
import { FrameScheduler } from './frame-scheduler'

export interface PipelineResult {
  readonly raw: RawReading
  readonly observation: ValidatedObservation
  readonly state: GameStateSnapshot
  readonly events: readonly GameEvent[]
  readonly hud: HudLocation
}

export class AnalysisPipeline {
  private readonly locator = new HudLocator()
  private readonly validator: ReadingValidator
  private readonly stateEngine: GameStateEngine
  private readonly recognizer: PresetRecognizer
  private scheduler: FrameScheduler | null = null
  private sessionId = ''

  constructor(
    private readonly preset: GamePreset,
    private profile: CaptureProfile,
    private readonly onResult: (result: PipelineResult) => void,
  ) {
    this.validator = new ReadingValidator(preset, profile.recognition.minConfidence)
    this.stateEngine = new GameStateEngine(preset)
    this.recognizer = preset.createRecognizer(profile)
  }

  start(video: HTMLVideoElement, sessionId: string, baseline: GameStateSnapshot | null = null): void {
    this.stop()
    this.sessionId = sessionId
    this.validator.resetBaseline(baseline)
    this.stateEngine.resetBaseline(baseline)
    this.scheduler = new FrameScheduler(video, captureIntervalMs(this.profile.recognition.captureFps))
    this.scheduler.start((capturedAt) => this.process(video, capturedAt))
  }

  updateProfile(profile: CaptureProfile): void {
    this.profile = profile
    this.validator.setMinimumConfidence(profile.recognition.minConfidence)
    this.scheduler?.setInterval(captureIntervalMs(profile.recognition.captureFps))
  }

  resetBaseline(): void {
    this.validator.resetBaseline()
    this.stateEngine.resetBaseline()
  }

  stop(): void {
    this.scheduler?.stop()
    this.scheduler = null
  }

  dispose(): void {
    this.stop()
    this.recognizer.dispose()
  }

  private async process(video: HTMLVideoElement, capturedAt: number): Promise<void> {
    if (!video.videoWidth || !video.videoHeight) return
    const bitmap = await createImageBitmap(video)
    try {
      const frame = drawSourceToImageData(bitmap, bitmap.width, bitmap.height)
      const hud = this.locator.locate(frame, this.preset)
      const rawResult = await this.recognizer.recognize({ frame, profile: this.profile, capturedAt, hudRect: hud.rect })
      const raw: RawReading = { ...rawResult, diagnostics: { ...rawResult.diagnostics, hudConfidence: hud.confidence } }
      const observation = this.validator.validate(raw)
      const { state, events } = this.stateEngine.apply(this.sessionId, observation)
      this.onResult({ raw, observation, state, events, hud })
    } finally {
      bitmap.close()
    }
  }
}
